import type { IPlayer } from "../../../jsidea.bundle.js";
import { type BlockEditProps, type BlockSettings } from "../../_core/BlockCore";
import { type Animation } from "./AnimationSupport";
import { AnimationTemplates } from "./AnimationTemplates.js";

declare global {
    class SplitType {
        constructor(element: HTMLElement, options: any);
        lines: HTMLElement[];
        chars: HTMLElement[];
        words: HTMLElement[];
        revert(): void;
    }
}

export interface AnimationCreateOptions {
    targets: HTMLElement[];
    timeline: gsap.core.Timeline;
    itemOptions: AnimationPlayerOptions;
}

interface AnimationRegistration {
    key: string;
    title: string;
    filter?: (options: {
        props: BlockEditProps<any>;
        settings: BlockSettings<any>;
    }) => boolean;
    create: (options: AnimationCreateOptions) => void;
    dispose?: (options: {
        targets: HTMLElement[];
        timeline: gsap.core.Timeline;
        itemOptions: AnimationPlayerOptions;
    }) => void;
}

interface TimelineItem {
    timeline: gsap.core.Timeline;
    dispose(): void;
}

declare global {
    interface WindowEventMap
        extends GlobalEventHandlersEventMap, WindowEventHandlersEventMap {
        "jsidea-register-animation": CustomEvent<{ view: AnimationView }>;
    }
}

export class AnimationView {
    public readonly registrations: { [key: string]: AnimationRegistration } =
        {};

    constructor() {
        console.log("[jsidea.core]", "[AnimationView]");

        AnimationTemplates.register(this);

        window.dispatchEvent(
            new CustomEvent("jsidea-register-animation", {
                bubbles: false,
                cancelable: false,
                detail: {
                    view: this,
                },
            }),
        );

        if (jsidea.wp.config.isAdmin) {
            return;
        }

        jsidea.component.register({
            selector: `[data-jsidea-animation]`,
            callback: (element: HTMLElement) => {
                const options: Animation = jsidea.merge<Animation>(
                    {
                        name: "fade-in",
                        once: true,
                        // duration: 1,
                        stagging: 0,
                        start: 0,
                        end: 1,
                    },
                    JSON.parse(element.getAttribute("data-jsidea-animation")),
                );

                new AnimationPlayer(element, this, options);

                element.classList.add("jsidea-animation--ready");
            },
        });
    }

    public register(registration: AnimationRegistration) {
        this.registrations[registration.key] = registration;
    }

    public create(
        animation: string,
        targets: HTMLElement | HTMLElement[],
        options?: AnimationPlayerOptions,
    ): TimelineItem | null {
        let timeline = gsap.timeline();
        timeline.pause();

        if (this.registrations[animation]) {
            this.registrations[animation].create({
                targets: Array.isArray(targets) ? targets : [targets],
                timeline: timeline,
                itemOptions: options,
            });
        } else {
            console.warn(`Animation "${animation}" not found.`);
            return null;
        }

        return {
            timeline,
            dispose: () => {
                timeline.kill();
                timeline = null;

                if (
                    this.registrations[animation] &&
                    typeof this.registrations[animation].dispose === "function"
                ) {
                    this.registrations[animation].dispose({
                        targets: Array.isArray(targets) ? targets : [targets],
                        timeline: timeline,
                        itemOptions: options,
                    });
                }
            },
        };
    }
}

interface AnimationPlayerOptions {
    kind?: string;
    duration?: number;
    once?: boolean;
    targets?: string;
    playerController?: string;
    maxTargets?: number;
    stagging?: number;
}

class AnimationPlayer implements IPlayer<AnimationPlayer> {
    public isReady: boolean = true;
    public onReady = jsidea.Signal.create<[AnimationPlayer]>();
    public onEnterFrame = jsidea.Signal.create<[AnimationPlayer]>();

    private _timeline: TimelineItem;
    private _controller: jsidea.PlayerController;
    private _element: HTMLElement;
    private _completed: number = 0;
    private _progress: number = 0;

    constructor(element: HTMLElement, view: AnimationView, options: Animation) {
        this._element = element;

        options = jsidea.merge<Animation>(
            {
                name: "fade-in",
                once: true,
                stagging: 0,
                start: 0,
                end: 1,
                duration: "0.7s",
                timeline: null,
                scrollView: null,
            },
            options,
        );

        if (options?.once && options?.timeline?.kind !== "viewport-in") {
            options.once = false;
        }

        let elements: HTMLElement[] = [element];
        if (options.stagging) {
            elements = Array.from(
                element.querySelectorAll(".jsidea-animation--stagging"),
            ) as HTMLElement[];
            if (elements.length === 0) {
                console.warn(
                    "Stagging is enabled but no elements with class .jsidea-animation--stagging found. Falling back to single element.",
                );
                elements = [element];
            }
            // options.stagging = 0.2;
        } else {
            options.stagging = undefined;
        }

        //TODO: default duration...
        let duration = 0.7;
        if (options.duration) {
            duration = parseFloat(options.duration);
            if (options.duration.endsWith("ms")) {
                duration = duration / 1000;
            }
        }

        // const stagging = (options.stagging * duration) / elements.length;
        this._timeline = view.create(options.name || "fade-in", elements, {
            ...options,
            stagging: options.stagging,
            duration: duration,
        });


        if(options.stagging){
            duration += (options.stagging * (elements.length - 1));
        }

        this._controller = new jsidea.PlayerController({
            once: options.once,
            start: options.start,
            end: options.end,
            duration: duration,
            loop: false, //TODO: options.loop,
            scrollView: options.scrollView,
            timeline: options.timeline,
            player: this,
            element,
        });
    }

    public get progress(): number {
        return this._progress;
    }

    public set progress(value: number) {
        value = jsidea.Calc.clamp(value, 0, 1);
        if (this._progress === value) {
            return;
        }
        this._progress = value;

        this._element.style.setProperty(
            "--jsidea-animation--progress",
            value.toString(),
        );

        if (this._timeline) {
            this._timeline.timeline.progress(value, false);
        }
        this.onEnterFrame.dispatch(this);

        if (value >= 1) {
            this._completed++;
            if (this._completed === 1) {
                //TODO: maybe dispose
            }
        }
    }

    public dispose(): void {
        this._controller.dispose();
        this._controller = null;
        if (this._timeline) {
            this._timeline.dispose();
        }
        this._timeline = null;
    }
}
